import React, { useMemo, useRef, useState } from 'react';
import { MdEdit, MdDelete } from 'react-icons/md';
import { toast } from 'react-toastify';
import DashboardLayout from './DashboardLayout';
import TablePagination from './TablePagination';
import { DEFAULT_PAGE_SIZE, getPaginationMeta } from '../../utils/pagination';

const inputClasses =
  'mt-1 w-full rounded-lg border border-gray-200 bg-[#F7E5D5] px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand';

const EMPTY_FORM = {
  date: '',
  itemCode: '',
  itemName: '',
  category: '',
  quantity: '',
  salesAmount: '',
};

function createRowId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function normalizeCsvHeader(header) {
  return String(header || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '');
}

function mapCsvRow(raw) {
  const get = (...keys) => {
    for (const key of keys) {
      const value = raw[key];
      if (value != null && String(value).trim() !== '') return String(value).trim();
    }
    return '';
  };

  return {
    id: createRowId(),
    date: get('date', 'sale_date', 'sales_date'),
    itemCode: get('item_code', 'code', 'product_code'),
    itemName: get('item_name', 'name', 'product_name'),
    category: get('category', 'product_category'),
    quantity: get('quantity', 'qty', 'sold_qty'),
    salesAmount: get('sales_amount', 'amount', 'sales', 'total_amount'),
  };
}

function parseCsvText(text) {
  const lines = String(text || '')
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  if (lines.length < 2) return [];

  const headers = lines[0].split(',').map(normalizeCsvHeader);
  const rows = [];

  for (let i = 1; i < lines.length; i += 1) {
    const values = lines[i].split(',').map((value) => value.trim());
    const raw = {};
    headers.forEach((header, index) => {
      raw[header] = values[index] ?? '';
    });
    const mapped = mapCsvRow(raw);
    if (mapped.itemCode || mapped.itemName) rows.push(mapped);
  }

  return rows;
}

function AdminDailySales() {
  const fileInputRef = useRef(null);
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const pageSize = DEFAULT_PAGE_SIZE;

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) =>
      [row.date, row.itemCode, row.itemName, row.category, row.quantity, row.salesAmount]
        .some((value) => String(value || '').toLowerCase().includes(q))
    );
  }, [rows, search]);

  const { totalCount, currentPage } = getPaginationMeta(
    filteredRows.length,
    page,
    pageSize
  );

  const pageItems = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredRows.slice(start, start + pageSize);
  }, [filteredRows, currentPage, pageSize]);

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
  };

  const openAddModal = () => {
    resetForm();
    setIsAddOpen(true);
  };

  const openEditModal = (row) => {
    setEditingId(row.id);
    setForm({
      date: row.date || '',
      itemCode: row.itemCode || '',
      itemName: row.itemName || '',
      category: row.category || '',
      quantity: row.quantity || '',
      salesAmount: row.salesAmount || '',
    });
    setIsAddOpen(true);
  };

  const closeModal = () => {
    setIsAddOpen(false);
    resetForm();
  };

  const handleSave = (event) => {
    event.preventDefault();
    if (!String(form.itemCode || '').trim() && !String(form.itemName || '').trim()) {
      toast.error('Item code or item name is required.');
      return;
    }

    const payload = {
      id: editingId || createRowId(),
      date: form.date.trim(),
      itemCode: form.itemCode.trim(),
      itemName: form.itemName.trim(),
      category: form.category.trim(),
      quantity: form.quantity.trim(),
      salesAmount: form.salesAmount.trim(),
    };

    if (editingId) {
      setRows((prev) => prev.map((row) => (row.id === editingId ? payload : row)));
      toast.success('Daily sales record updated.');
    } else {
      setRows((prev) => [payload, ...prev]);
      toast.success('Daily sales record added.');
    }

    closeModal();
  };

  const handleDelete = (id) => {
    setRows((prev) => prev.filter((row) => row.id !== id));
    toast.success('Record deleted.');
  };

  const handleCsvUpload = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast.error('Please upload a CSV file.');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = parseCsvText(reader.result);
        if (parsed.length === 0) {
          toast.error('No valid rows found in CSV.');
          return;
        }
        setRows((prev) => [...parsed, ...prev]);
        setPage(1);
        toast.success(`${parsed.length} record(s) imported from CSV.`);
      } catch {
        toast.error('Could not read CSV file.');
      }
    };
    reader.onerror = () => toast.error('Could not read CSV file.');
    reader.readAsText(file);
  };

  return (
    <DashboardLayout title="Daily Sales">
      <div className="space-y-4 py-4">
        <div className="rounded-2xl bg-white p-4 shadow-sm sm:p-6">
          <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="relative w-full min-w-0 md:max-w-xs">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              </span>
              <input
                type="text"
                placeholder="Search daily sales"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="w-full rounded-lg border border-gray-200 bg-[#faf8f5] py-2 pl-10 pr-3 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
              />
            </div>

            <div className="flex w-full shrink-0 flex-col gap-2 sm:flex-row sm:items-center sm:justify-end md:w-auto">
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={handleCsvUpload}
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50 sm:w-auto"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
                Upload CSV
              </button>
              <button
                type="button"
                onClick={openAddModal}
                className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white shadow-sm sm:w-auto"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                Add Daily Sales
              </button>
            </div>
          </div>

          {!filteredRows.length ? (
            <p className="py-8 text-center text-sm text-gray-500">
              No daily sales records yet. Add manually or upload a CSV file.
            </p>
          ) : (
            <>
              <ul className="mb-4 space-y-3 md:hidden">
                {pageItems.map((row, index) => (
                  <li key={row.id} className="rounded-lg border border-gray-100 bg-[#faf8f5] p-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="text-xs text-gray-500">
                          #{(currentPage - 1) * pageSize + index + 1}
                        </p>
                        <p className="mt-1 text-sm font-semibold text-gray-900">{row.itemName || '—'}</p>
                        <p className="mt-1 text-xs text-gray-600">Code: {row.itemCode || '—'}</p>
                        <p className="mt-1 text-xs text-gray-600">Date: {row.date || '—'}</p>
                        <p className="mt-1 text-xs text-gray-600">Category: {row.category || '—'}</p>
                        <p className="mt-1 text-xs text-gray-600">
                          Qty: {row.quantity || '—'} · Amount: {row.salesAmount || '—'}
                        </p>
                      </div>
                      <div className="flex shrink-0 items-center gap-1">
                        <button
                          type="button"
                          onClick={() => openEditModal(row)}
                          className="rounded-lg p-1.5 text-red-700 hover:bg-white"
                          aria-label="Edit record"
                        >
                          <MdEdit className="h-5 w-5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(row.id)}
                          className="rounded-lg p-1.5 text-red-700 hover:bg-white"
                          aria-label="Delete record"
                        >
                          <MdDelete className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>

              <div className="hidden overflow-x-auto md:block">
                <table className="min-w-full border-collapse text-sm">
                  <thead>
                    <tr>
                      <th className="whitespace-nowrap border-b border-gray-200 px-3 py-2 text-left font-semibold text-gray-700">
                        Sr. No.
                      </th>
                      <th className="border-b border-gray-200 px-3 py-2 text-left font-semibold text-gray-700">
                        Date
                      </th>
                      <th className="border-b border-gray-200 px-3 py-2 text-left font-semibold text-gray-700">
                        Item Code
                      </th>
                      <th className="border-b border-gray-200 px-3 py-2 text-left font-semibold text-gray-700">
                        Item Name
                      </th>
                      <th className="border-b border-gray-200 px-3 py-2 text-left font-semibold text-gray-700">
                        Category
                      </th>
                      <th className="border-b border-gray-200 px-3 py-2 text-left font-semibold text-gray-700">
                        Quantity
                      </th>
                      <th className="border-b border-gray-200 px-3 py-2 text-left font-semibold text-gray-700">
                        Sales Amount
                      </th>
                      <th className="border-b border-gray-200 px-3 py-2 text-left font-semibold text-gray-700">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageItems.map((row, index) => (
                      <tr key={row.id} className="hover:bg-amber-50/40">
                        <td className="whitespace-nowrap border-b border-gray-100 px-3 py-2 text-gray-600">
                          {(currentPage - 1) * pageSize + index + 1}
                        </td>
                        <td className="border-b border-gray-100 px-3 py-2">{row.date || '—'}</td>
                        <td className="border-b border-gray-100 px-3 py-2">{row.itemCode || '—'}</td>
                        <td className="border-b border-gray-100 px-3 py-2">{row.itemName || '—'}</td>
                        <td className="border-b border-gray-100 px-3 py-2">{row.category || '—'}</td>
                        <td className="border-b border-gray-100 px-3 py-2">{row.quantity || '—'}</td>
                        <td className="border-b border-gray-100 px-3 py-2">{row.salesAmount || '—'}</td>
                        <td className="border-b border-gray-100 px-3 py-2">
                          <div className="flex items-center gap-1">
                            <button
                              type="button"
                              onClick={() => openEditModal(row)}
                              className="rounded-md p-1 text-red-700 hover:bg-amber-50/60"
                              aria-label="Edit record"
                            >
                              <MdEdit className="h-5 w-5" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDelete(row.id)}
                              className="rounded-md p-1 text-red-700 hover:bg-amber-50/60"
                              aria-label="Delete record"
                            >
                              <MdDelete className="h-5 w-5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <TablePagination
                totalCount={totalCount}
                currentPage={currentPage}
                onPageChange={setPage}
                pageSize={pageSize}
              />
            </>
          )}
        </div>
      </div>

      {isAddOpen ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
          role="dialog"
          aria-modal="true"
          aria-label={editingId ? 'Edit daily sales record' : 'Add daily sales record'}
          onClick={closeModal}
        >
          <div
            className="w-full max-w-lg rounded-2xl bg-white p-5 shadow-2xl sm:p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4 flex items-center justify-between gap-3">
              <h2 className="text-lg font-semibold text-gray-900">
                {editingId ? 'Edit Daily Sales' : 'Add Daily Sales'}
              </h2>
              <button
                type="button"
                onClick={closeModal}
                className="flex h-9 w-9 items-center justify-center rounded-lg text-2xl text-brand hover:bg-gray-100"
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSave} className="space-y-4">
              <label className="flex flex-col text-sm text-gray-700">
                Date
                <input
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm((prev) => ({ ...prev, date: e.target.value }))}
                  className={inputClasses}
                />
              </label>
              <label className="flex flex-col text-sm text-gray-700">
                Item Code
                <input
                  type="text"
                  value={form.itemCode}
                  onChange={(e) => setForm((prev) => ({ ...prev, itemCode: e.target.value }))}
                  className={inputClasses}
                  placeholder="Enter item code"
                />
              </label>
              <label className="flex flex-col text-sm text-gray-700">
                Item Name
                <input
                  type="text"
                  value={form.itemName}
                  onChange={(e) => setForm((prev) => ({ ...prev, itemName: e.target.value }))}
                  className={inputClasses}
                  placeholder="Enter item name"
                />
              </label>
              <label className="flex flex-col text-sm text-gray-700">
                Category
                <input
                  type="text"
                  value={form.category}
                  onChange={(e) => setForm((prev) => ({ ...prev, category: e.target.value }))}
                  className={inputClasses}
                  placeholder="Enter category"
                />
              </label>
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="flex flex-col text-sm text-gray-700">
                  Quantity
                  <input
                    type="number"
                    min="0"
                    value={form.quantity}
                    onChange={(e) => setForm((prev) => ({ ...prev, quantity: e.target.value }))}
                    className={inputClasses}
                    placeholder="0"
                  />
                </label>
                <label className="flex flex-col text-sm text-gray-700">
                  Sales Amount
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.salesAmount}
                    onChange={(e) => setForm((prev) => ({ ...prev, salesAmount: e.target.value }))}
                    className={inputClasses}
                    placeholder="0.00"
                  />
                </label>
              </div>

              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={closeModal}
                  className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white"
                >
                  {editingId ? 'Update' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </DashboardLayout>
  );
}

export default AdminDailySales;
